import React, { useEffect } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import Loading from 'Components/Loading';
import ApplicationLayout from 'Components/ApplicationLayout';
import pMinDelay from 'p-min-delay';
import { useSelector } from 'react-redux';
import NoSSR from 'Components/NoSSR';
import { getMatchCardHeader } from 'Store/actions';
import EnvironmentManager from 'Utils/environmentManager';
import { toAbsoluteMediaUrl } from 'Utils/toAbsoluteMediaUrl';

const MatchCard = loadable(() => pMinDelay(import('./MatchCard'), 300), {
  fallback: <Loading />
});

const BAHISTA_TAB_SLUGS = ['bahista-yapay-zeka-iddaa-tahminleri', 'bahista'];
const BAHISTA_SEO_TITLE_SUFFIX = 'Bahista Yapay Zeka İddaa Tahminleri';
const BAHISTA_SEO_DESCRIPTION = 'Yapay Zeka Yazarımız Bahista’nın İddaa Tahminleri Bilyoner’de Seni Bekliyor! Bahista’nın Detaylı Analizleri Sonucunda Ortaya Çıkan Yüksek Olasılıklı İddaa Tahminlerini Hemen İncele!';
const SEO_META_KEYS_TO_REPLACE = new Set(['description', 'og:title', 'og:description']);

const filterReplacedMeta = (meta = []) => meta.filter(item => {
  const key = item?.property || item?.name;
  return !SEO_META_KEYS_TO_REPLACE.has(key);
});

const getBahistaMatchLabel = (headerData, eventData) => {
  const homeTeam = headerData?.homeTeam?.teamName || eventData?.htn || '';
  const awayTeam = headerData?.awayTeam?.teamName || eventData?.atn || '';
  if (homeTeam && awayTeam) return `${homeTeam} - ${awayTeam}`;
  return headerData?.eventName || eventData?.en || '';
};

const getBahistaPageTitle = matchLabel => (
  matchLabel ? `${matchLabel} | ${BAHISTA_SEO_TITLE_SUFFIX}` : BAHISTA_SEO_TITLE_SUFFIX
);

const applyBahistaSeoToDom = (title, description, shareImageUrl) => {
  if (typeof document === 'undefined') return;

  document.title = title;

  const upsertMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  upsertMeta('name', 'description', description);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  if (shareImageUrl) {
    upsertMeta('property', 'og:image', shareImageUrl);
    upsertMeta('name', 'twitter:image', shareImageUrl);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
  }
};

export const loadData = ({ params } = {}) => {
  const eventId = params?.eventId;
  const tabName = params?.tabName;
  if (!eventId || !BAHISTA_TAB_SLUGS.includes(tabName)) return [];

  return [
    async dispatch => {
      try {
        await dispatch(getMatchCardHeader(eventId));
      } catch (e) {
        return null;
      }
    },
  ];
};

const MatchCardRouter = props => {
  const metaConfiguration = props.metaConfiguration['/mac-karti'];
  const meta = metaConfiguration.meta;
  const tabName = props.match?.params?.tabName;
  const eventData = useSelector(state => state.matchCard.event?.data);
  const headerData = useSelector(state => state.matchCard.header?.data);
  const matchLabel = getBahistaMatchLabel(headerData, eventData);
  const isBahistaTab = BAHISTA_TAB_SLUGS.includes(tabName);
  const bahistaShareOgImage = isBahistaTab
    ? toAbsoluteMediaUrl(EnvironmentManager.getMessageText({ key: 'match_card_bahista_share_image' }))
    : '';
  // eslint-disable-next-line no-nested-ternary
  const pageTitle = isBahistaTab
    ? getBahistaPageTitle(matchLabel)
    : (matchLabel ? `${matchLabel} | Iddaa Oranlari` : metaConfiguration?.title);
  const pageDescription = isBahistaTab
    ? BAHISTA_SEO_DESCRIPTION
    : `${matchLabel} maci kadrolar, iddaa oranlari, tahminleri, mac raporu, istatistikleri Bilyoner de!`;

  useEffect(() => {
    if (!isBahistaTab) return undefined;
    applyBahistaSeoToDom(pageTitle, pageDescription, bahistaShareOgImage);
    return undefined;
  }, [isBahistaTab, pageTitle, pageDescription, bahistaShareOgImage]);

  const pageConfig = {
    headerType: 'full',
    footerType: 'none',
    helmet: {
      bodyAttributes: {
        class: `${props.userAgent.device?.type} bg-green match-card-page`
      },
      title: pageTitle,
      meta: [
        ...filterReplacedMeta(meta),
        { name: 'description', content: pageDescription },
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        ...(bahistaShareOgImage ? [{ property: 'og:image', content: bahistaShareOgImage }] : []),
      ],
      script: [
        { defer: 'defer', src: '/public/thirdParty/swiper/swiper.js' },
      ],
      link: [
        { rel: 'stylesheet', type: 'text/css', href: '/public/thirdParty/swiper/swiper.css' },
        { rel: 'stylesheet', type: 'text/css', href: '/public/thirdParty/video-js/video-js.min.css' }
      ]
    }
  };

  return (
    <ApplicationLayout { ...props } { ...pageConfig }>
      <NoSSR onSSR={ <Loading /> }>
        <MatchCard { ...props } />
      </NoSSR>
    </ApplicationLayout>
  );
};

MatchCardRouter.propTypes = {
  userAgent: PropTypes.object,
  metaConfiguration: PropTypes.object,
  match: PropTypes.shape({
    params: PropTypes.shape({
      tabName: PropTypes.string,
      eventId: PropTypes.string,
    }),
  }),
};

export default MatchCardRouter;
